import React, { useEffect, useState } from 'react';
import { AlertCircle, ArrowDownLeft, Plus, Trash2 } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentMethods } from '../../../../../hooks/usePaymentMethods';
import { BankAccountRecord, listBankAccounts } from '../../../../../services/BankAccountsService';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import { JournalLine, postJournalEntry } from '../../../../../services/JournalEntriesService';
import { formatEuro } from '../masters/financeMasters';
import { FormSkeleton, JournalPreview, SearchSelect, today } from './FormParts';
import '../../shared-modules.css';
import './FinanceForms.css';

const round2 = (n: number) => Math.round(n * 100) / 100;

interface DepositLine {
  id: string;
  receivedFrom: string;
  account: string;
  description: string;
  paymentMethod: string;
  reference: string;
  amount: number;
}

const newDepositLine = (paymentMethod = 'Transferencia bancaria'): DepositLine => ({
  id: String(Date.now() + Math.random()),
  receivedFrom: '',
  account: '',
  description: '',
  paymentMethod,
  reference: '',
  amount: 0
});

interface BankDepositFormProps {
  privacyMode: boolean;
  onDone?: () => void;
}

export const BankDepositForm = ({ privacyMode, onDone }: BankDepositFormProps) => {
  const { notify } = useToast();

  const { methods } = usePaymentMethods();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [banks, setBanks] = useState<BankAccountRecord[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);

  const [bankId, setBankId] = useState('');
  const [date, setDate] = useState(today());
  const [lines, setLines] = useState<DepositLine[]>([newDepositLine()]);

  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [bankList, chart] = await Promise.all([listBankAccounts(), listChartOfAccounts()]);
        if (cancelled) return;
        setBanks(bankList);
        setBankId(bankList.length > 0 ? bankList[0].id : '');
        setAccounts(chart);
        setLoadError('');
      } catch (err) {
        console.error('Error loading bank deposit form data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar las cuentas bancarias y el plan de cuentas.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const updateLine = (id: string, patch: Partial<DepositLine>) =>
    setLines(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l)));

  const bank = banks.find(b => b.id === bankId);
  const total = round2(lines.reduce((s, l) => s + l.amount, 0));

  const accountName = (code: string) => {
    const account = accounts.find(a => a.code === code);
    return account ? account.name : 'Contrapartida';
  };

  const journalLines: JournalLine[] = [
    { account: '572000', accountName: bank ? `Bancos c/c (${bank.name})` : 'Bancos c/c', debit: total, credit: 0 },
    ...lines.map(l => ({ account: l.account, accountName: accountName(l.account), debit: 0, credit: l.amount }))
  ];

  const validate = () => {
    if (!bank) return 'Seleccione la cuenta destino.';
    if (!date) return 'La fecha es obligatoria.';
    if (lines.some(l => !l.receivedFrom.trim())) return 'Indique de quién se recibe cada importe.';
    if (lines.some(l => !l.account)) return 'Todas las líneas necesitan una cuenta contable.';
    if (lines.some(l => l.account.startsWith('572'))) return 'La contrapartida no puede ser la propia cuenta de bancos (572).';
    if (lines.some(l => l.amount <= 0)) return 'Todas las líneas necesitan un importe mayor que cero.';
    return '';
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const origin = lines.length === 1 ? lines[0].receivedFrom.trim() : `${lines.length} ingresos`;
      await postJournalEntry(`Depósito bancario en ${bank.name}: ${origin}`, journalLines, 'Depósitos Bancarios', accounts, date);
    } catch (err) {
      console.error('Error posting bank deposit journal entry:', err);
      setFormError(`No se pudo contabilizar el depósito: ${err instanceof Error ? err.message : 'error del servidor.'}`);
      setSaving(false);
      return;
    }
    notify('Depósito contabilizado', `Depósito de ${formatEuro(total, privacyMode)} en ${bank.name} contabilizado en el Libro Diario [572 a contrapartida].`, 'info');
    setSaving(false);
    if (onDone) onDone();
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const accountOptions = accounts
    .filter(a => a.code.length >= 3 && !a.code.startsWith('572'))
    .map(a => ({ value: a.code, label: `${a.code} · ${a.name}` }));

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">TESORERÍA Y BANCOS • DEPÓSITOS</span>
          <h3 className="fin-doc__title"><ArrowDownLeft size={14} /> Depósito bancario</h3>
        </div>
        <span className="fin-doc__badge">572000 A CONTRAPARTIDA</span>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cuenta destino *</label>
          <select className="sidebar-form-input" value={bankId} onChange={(e) => setBankId(e.target.value)}>
            {banks.length === 0 && <option value="">No hay cuentas bancarias</option>}
            {banks.map(b => <option key={b.id} value={b.id}>572000 · {b.name}</option>)}
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>

      <div className="fin-lines">
        <div className="fin-lines__header">
          <span>Líneas del depósito</span>
          <button type="button" className="fin-lines__add" onClick={() => setLines([...lines, newDepositLine(lines[lines.length - 1].paymentMethod)])}>
            <Plus size={12} /> AÑADIR LÍNEA
          </button>
        </div>

        <div className="fin-lines__row fin-lines__row--head fin-lines__row--deposit">
          <span>Recibido de</span>
          <span>Cuenta contable</span>
          <span>Descripción</span>
          <span>Forma de pago</span>
          <span>Referencia</span>
          <span className="right">Importe (€)</span>
          <span />
        </div>

        {lines.map(line => (
          <div key={line.id} className="fin-lines__row fin-lines__row--deposit">
            <input
              type="text"
              className="sidebar-form-input"
              value={line.receivedFrom}
              onChange={(e) => updateLine(line.id, { receivedFrom: e.target.value })}
              placeholder="Socio, cliente, caja..."
            />
            <SearchSelect
              options={accountOptions}
              value={line.account}
              onChange={(account) => updateLine(line.id, { account })}
              placeholder="Buscar cuenta..."
              emptyText="No hay cuentas"
            />
            <input
              type="text"
              className="sidebar-form-input"
              value={line.description}
              onChange={(e) => updateLine(line.id, { description: e.target.value })}
              placeholder="Concepto del ingreso"
            />
            <select
              className="sidebar-form-input"
              value={line.paymentMethod}
              onChange={(e) => updateLine(line.id, { paymentMethod: e.target.value })}
            >
              {methods.filter(m => m.isActive || m.name === line.paymentMethod).map(m => (
                <option key={m.id} value={m.name}>{m.name}</option>
              ))}
            </select>
            <input
              type="text"
              className="sidebar-form-input"
              value={line.reference}
              onChange={(e) => updateLine(line.id, { reference: e.target.value })}
              placeholder="Ref."
            />
            <input
              type="number"
              min={0}
              step="0.01"
              className="sidebar-form-input right"
              value={line.amount}
              onChange={(e) => updateLine(line.id, { amount: parseFloat(e.target.value) || 0 })}
            />
            <button
              type="button"
              className="fin-lines__remove"
              title="Eliminar línea"
              disabled={lines.length === 1}
              onClick={() => setLines(lines.filter(l => l.id !== line.id))}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      <div className="fin-doc__totals">
        <span>Líneas: <strong>{lines.length}</strong></span>
        <span className="fin-doc__total">Total depositado: <strong>{formatEuro(total, privacyMode)}</strong></span>
      </div>

      <JournalPreview lines={journalLines} privacyMode={privacyMode} />

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn" disabled title="Pendiente de endpoint">
          Guardar
        </button>
        <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled={saving} onClick={handleSubmit}>
          {saving ? 'Contabilizando...' : 'Contabilizar'}
        </button>
      </div>
    </div>
  );
};

export default BankDepositForm;
